import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InjectLocalStorage, InjectQueryParam, InjectSessionStorage, LocalStorageKey, SessionStorageKey } from '@ddt-web/generic-utils';
import { DebugQueryParamKey, SharedQueryParamKey, UrlUtils } from '@ddt-web/url';
import * as isNil from 'lodash/isNil';
import * as set from 'lodash/set';
import { Observable, of } from 'rxjs';

import * as mockAssetResolvePcRawData from '../../../../../test_data/dummy-backend-data/asset-resolve-pc.json';
import * as mockModelFamiliesRawData from '../../../../../test_data/dummy-backend-data/model-families.json';
import * as mockModelsAnalyticsRawData from '../../../../../test_data/dummy-backend-data/models-analytics.json';
import * as mockModelsBatchGetRawData from '../../../../../test_data/dummy-backend-data/models-batch-get.json';
import * as mockAssetResolveIdeasRawData from '../../../../../test_data/dummy-backend-data/pas-asset-resolve/asset-resolve-blk-ideas.json';
import * as mockAssetResolveSecurityDetailsCompareRawData from '../../../../../test_data/dummy-backend-data/pas-asset-resolve/asset-resolve-security-details-compare.json';
import * as mockAssetResolveSecuritiesRawData from '../../../../../test_data/dummy-backend-data/pas-asset-resolve/asset-resolve-security-details.json';
import * as mockAssetResolveTaxRawData from '../../../../../test_data/dummy-backend-data/pas-asset-resolve/asset-resolve-tax.json';
import * as mockAssetResolveIqSecurityDetailsRawData from '../../../../../test_data/dummy-backend-data/pas-asset-resolve/iq-asset-resolve-security-details.json';
import * as mockPortfolioAnalyticsRawData from '../../../../../test_data/dummy-backend-data/portfolio-analytics.json';

@Injectable()
export class PasInterceptorService implements HttpInterceptor {

	@InjectQueryParam(DebugQueryParamKey.USE_PAS_BETA) private readonly usePasBeta: string;

	// Can be used in preview to override the published date when we need to test a new date before flipping
	@InjectQueryParam(SharedQueryParamKey.PAS_AS_OF_DATE) private readonly pasAsOfDateOverrideQp: string;
	@InjectSessionStorage(SessionStorageKey.PAS_AS_OF_DATE) private pasAsOfDateOverrideSession: string;

	@InjectQueryParam(DebugQueryParamKey.PAS_MOCK_API) private readonly pasMockApiQp: boolean;
	@InjectLocalStorage(LocalStorageKey.MockPasApi) private readonly pasMockApiLocalStorage: boolean;

	private readonly asOfDateRegex = new RegExp(/^\d{4}-\d{2}-\d{2}$/);

	private readonly MOCK_PAS_APIS = [
		{ api: 'assets:resolve', rc: 'tst:ac:te-blk-ideas' },
		{ api: 'assets:resolve', rc: 'tst:pc:security-details' },
		{ api: 'assets:resolve', rc: 'tst:ac:iq-security-details' },
		{ api: 'assets:resolve', rc: 'tst:ac:iq-security-details-compare' },
		{ api: 'assets:resolve', rc: 'tst:ac:tax', pkg: 'AC_TAX' },
		{ api: 'assets:resolve', rc: 'tst:ac:pa-security-details' },
		{ api: 'assets:resolve', rc: 'tst:ac:te-blk-ideas' }
	];


	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

		const isPasApiRequest = request.url.includes('/api/wealth/distribution/');
		if (!isPasApiRequest) {
			return next.handle(request);
		}

		const mockResponse = this.getMockResponse(request);
		if (mockResponse) { return mockResponse; }

		request = this.overrideAsOfDate(request);
		request = this.addChannelHeader(request);

		return next.handle(request);
	}

	private addChannelHeader(request: HttpRequest<any>): HttpRequest<any> {
		const isBetaApp = this.usePasBeta === 'true' || this.usePasBeta === null && UrlUtils.isProduction() && window.location.href.includes('-beta');
		if (isBetaApp && this.usePasBeta !== 'false') {
			const requestCloned = request.clone({
				setHeaders: {
					'VND.Com.blackrock.Channel': 'beta'
				}
			});
			return requestCloned;
		}

		return request;
	}

	private overrideAsOfDate(request: HttpRequest<any>): HttpRequest<any> {
		// Using session storage because as you move from one screen to another, the qp goes away.
		// If you want to clear the sessionStorage value, use ?pasAsOfDate=null or open new tab
		const pasAsOfDateOverride = this.pasAsOfDateOverrideQp || this.pasAsOfDateOverrideSession;
		if (pasAsOfDateOverride && isNil(request.body?.asOfDate)) {
			if (this.asOfDateRegex.test(pasAsOfDateOverride)) {
				this.pasAsOfDateOverrideSession = pasAsOfDateOverride;
				set(request, 'body.asOfDate', pasAsOfDateOverride);
			} else {
				console.error('pasAsOfDate override format is incorrect');
			}
		}

		if (this.pasAsOfDateOverrideQp === 'null') {
			this.pasAsOfDateOverrideSession = null;
		}

		return request;
	}

	/*******************************************************
	 * Mock responses
	 *******************************************************/

	private getMockResponse(request: HttpRequest<any>): Observable<HttpEvent<any>> {
		const isPasMockEnabled = this.pasMockApiQp || this.pasMockApiLocalStorage;
		if (!isPasMockEnabled) {
			return;
		}

		// mock pas specific apis
		const pasMockApiIndex = this.MOCK_PAS_APIS.findIndex(row => request.url.includes(row.api) && (request.body?.rc?.includes(row.rc) || request.body?.pkg?.includes(row.pkg)));
		if (pasMockApiIndex !== -1) {
			return this.mockAssetResolveResponse(pasMockApiIndex);
		}

		const isModelFamilies = request.url.includes('/wealth/distribution/models/v1/models/families');
		if (isModelFamilies) {
			return of(new HttpResponse({ body: mockModelFamiliesRawData }));
		}

		const isModelBatchGet = request.url.includes(':batchGet');
		if (isModelBatchGet) {
			return of(new HttpResponse({ body: mockModelsBatchGetRawData }));
		}

		const isPortfolioAnalytics = request.url.includes(':computePortfolioAnalytics');
		if (isPortfolioAnalytics) {
			return of(new HttpResponse({ body: mockPortfolioAnalyticsRawData }));
		}

		const isModelsAnalytics = request.url.includes(':computeModelFamilyAnalytics');
		if (isModelsAnalytics) {
			return of(new HttpResponse({ body: mockModelsAnalyticsRawData }));
		}
	}

	private mockAssetResolveResponse(index: number): Observable<HttpEvent<any>> {
		let response;
		if (this.MOCK_PAS_APIS[index].rc === 'tst:ac:tax') {
			response = new HttpResponse({ body: mockAssetResolveTaxRawData });
		}
		if (this.MOCK_PAS_APIS[index].rc === 'tst:ac:pa-security-details') {
			response = new HttpResponse({ body: mockAssetResolveSecuritiesRawData });
		}
		if (this.MOCK_PAS_APIS[index].rc === 'tst:ac:te-blk-ideas') {
			response = new HttpResponse({ body: mockAssetResolveIdeasRawData });
		}
		if (this.MOCK_PAS_APIS[index].rc === 'tst:pc:security-details') {
			response = new HttpResponse({ body: mockAssetResolvePcRawData });
		}
		if (this.MOCK_PAS_APIS[index].rc === 'tst:ac:iq-security-details') {
			response = new HttpResponse({ body: mockAssetResolveIqSecurityDetailsRawData });
		}
		if (this.MOCK_PAS_APIS[index].rc === 'tst:ac:iq-security-details-compare') {
			response = new HttpResponse({ body: mockAssetResolveSecurityDetailsCompareRawData });
		}
		return of(response);
	}

}
